import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import TrialOutreachPill from '../../trial-outreach/components/TrialOutreachPill';
import { CATEGORY_OPTIONS, labelOf, statusOption, instagramProfileLink } from '../prospectOptions';

/**
 * 'Y-m-d H:i:s' -> "16/10/2026".
 * @param {string|null} value
 * @returns {string}
 */
function fmtDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR');
}

/**
 * @param {number|null} days dias desde o último contato (days_since_contact)
 * @returns {string}
 */
function daysLabel(days) {
  if (days === null || days === undefined) return '';
  if (days === 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}

/**
 * ProspectRow - Um contato da Prospecção
 * @param {Object} props
 * @param {Object} props.item linha de admin/prospects/list.php
 * @param {boolean} props.selected
 * @param {boolean} [props.selectable] false esconde a coluna de marcar (fila de quem respondeu)
 * @param {Function} props.onToggle
 * @param {Function} props.onMessage
 * @param {Function} props.onEdit
 */
function ProspectRow({ item, selected, selectable = true, onToggle, onMessage, onEdit }) {
  const status = statusOption(item.status);
  const place = [item.city, item.state].filter(Boolean).join(' - ');
  const contactCaption = [
    item.messages_count > 0 && (item.messages_count === 1 ? '1 mensagem' : `${item.messages_count} mensagens`),
    daysLabel(item.days_since_contact),
  ].filter(Boolean).join(' · ');

  return (
    <TableRow hover selected={selected}>
      {selectable && (
        <TableCell padding="checkbox">
          {item.whatsapp ? (
            <Checkbox size="small" checked={selected} onChange={() => onToggle(item.id)} inputProps={{ 'aria-label': `Selecionar ${item.name}` }} />
          ) : (
            <Tooltip title="Sem WhatsApp: use Escrever mensagem">
              <Box component="span"><Checkbox size="small" disabled /></Box>
            </Tooltip>
          )}
        </TableCell>
      )}
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {labelOf(CATEGORY_OPTIONS, item.category)}{place ? ` · ${place}` : ''}
        </Typography>
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {item.whatsapp && <Typography variant="body2">{item.whatsapp}</Typography>}
          {item.instagram && (
            <Link href={instagramProfileLink(item.instagram)} target="_blank" rel="noopener" variant="body2" underline="hover">
              @{item.instagram}
            </Link>
          )}
          {item.email && <Typography variant="caption" color="text.secondary">{item.email}</Typography>}
          {!item.whatsapp && !item.instagram && !item.email && (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
        </Box>
      </TableCell>

      <TableCell>
        {item.note ? (
          <Tooltip title={item.note}>
            <Box component="span"><TrialOutreachPill tone={status.tone} label={status.label} /></Box>
          </Tooltip>
        ) : (
          <TrialOutreachPill tone={status.tone} label={status.label} />
        )}
        {item.last_reply_text && (
          <Tooltip title={item.last_reply_text}>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.5, maxWidth: 300 }}>
              Respondeu {fmtDate(item.last_reply_at)}: «{item.last_reply_text}»
            </Typography>
          </Tooltip>
        )}
        {item.referred_by_company_id !== null && item.referred_by_company_id !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Indicação de{' '}
            <Link component={RouterLink} to={`/admin/companies/${item.referred_by_company_id}`} underline="hover">
              {item.referred_by_company_name || `Empresa ${item.referred_by_company_id}`}
            </Link>
          </Typography>
        )}
        {item.company_id !== null && item.company_id !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Criou conta em {fmtDate(item.signed_up_at)}:{' '}
            <Link component={RouterLink} to={`/admin/companies/${item.company_id}`} underline="hover">
              {item.company_name || `Empresa ${item.company_id}`}
            </Link>
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2">{fmtDate(item.last_contacted_at)}</Typography>
        {contactCaption && (
          <Typography variant="caption" color="text.secondary">{contactCaption}</Typography>
        )}
      </TableCell>

      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip title="Escrever mensagem">
          <IconButton size="small" color="primary" onClick={() => onMessage(item)} aria-label="Escrever mensagem">
            <SendOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit(item)} aria-label="Editar">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default ProspectRow;
